import { useState, useEffect } from 'react'
import { Button } from '../ui/Button'
import { Badge } from '../ui/Badge'
import { Card } from '../ui/Card'
import { FolderOpen, LayoutGrid, X, AlertCircle } from 'lucide-react'
import { apiClient } from '../../services/api'

interface Category {
  id: string
  name: string
  description?: string
  productCount?: number
}

interface Product {
  id: string
  name: string
  sku: string
  price: number | string
  stock_quantity: number
  category_name?: string
  category_id?: string
}

interface CategorySidenavProps {
  products: Product[]
  onCategorySelect: (categoryName: string | null) => void
  selectedCategory: string | null
  isCollapsed: boolean
  onToggle: () => void
}

export const CategorySidenav = ({ 
  products, 
  onCategorySelect, 
  selectedCategory, 
  isCollapsed,
  onToggle 
}: CategorySidenavProps) => {
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadCategories = async () => {
      try {
        // Fetch all categories from API
        const response = await apiClient.getCategories()
        const allCategories = response.categories || response.data?.categories || []
        
        // Count products for each category
        const categoriesWithCounts = allCategories.map(category => {
          const productCount = products.filter(product => 
            product.category_name === category.name
          ).length
          
          return {
            id: category.id,
            name: category.name,
            description: category.description,
            productCount
          }
        })
        
        // Also add any categories that exist in products but not in the API response
        const existingCategoryNames = new Set(allCategories.map(c => c.name))
        const productCategories = new Set()
        products.forEach(product => {
          if (product.category_name && !existingCategoryNames.has(product.category_name) && !productCategories.has(product.category_name)) {
            productCategories.add(product.category_name)
            categoriesWithCounts.push({
              id: product.category_id || `product-category-${product.category_name}`,
              name: product.category_name,
              description: 'Product category',
              productCount: products.filter(p => p.category_name === product.category_name).length
            })
          }
        })
        
        setCategories(categoriesWithCounts)
      } catch (error) {
        // Fallback to extracting categories from products only
        const categoryMap = new Map<string, Category>()
        
        products.forEach(product => {
          if (product.category_name) {
            const category = categoryMap.get(product.category_name) || {
              id: product.category_id || `fallback-category-${product.category_name}`,
              name: product.category_name,
              productCount: 0
            }
            category.productCount = (category.productCount || 0) + 1
            categoryMap.set(product.category_name, category)
          }
        })

        setCategories(Array.from(categoryMap.values()))
      } finally {
        setLoading(false)
      }
    }

    loadCategories()
  }, [products])

  const totalProducts = products.length
  const selectedCategoryCount = selectedCategory 
    ? products.filter(p => p.category_name === selectedCategory).length 
    : totalProducts

  if (isCollapsed) {
    return (
      <div className="w-12 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col items-center py-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggle}
          className="mb-4"
        >
          <LayoutGrid className="h-4 w-4" />
        </Button>
        
        <div className="space-y-2">
          <Button
            variant={!selectedCategory ? "default" : "ghost"}
            size="sm"
            onClick={() => onCategorySelect(null)}
            className="w-8 h-8 p-0"
            title="All Categories"
          >
            <FolderOpen className="h-3 w-3" />
          </Button>
          
          {categories.slice(0, 5).map(category => (
            <Button
              key={category.id}
              variant={selectedCategory === category.name ? "default" : "ghost"}
              size="sm"
              onClick={() => onCategorySelect(category.name)}
              className="w-8 h-8 p-0"
              title={category.name}
            >
              {category.name.charAt(0).toUpperCase()}
            </Button>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <FolderOpen className="h-5 w-5" />
            Categories
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggle}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {selectedCategoryCount} of {totalProducts} products
        </div>
      </div>

      {/* Categories List */}
      <div className="flex-1 overflow-auto p-4 space-y-2">
        {/* All Categories */}
        <Card 
          className={`p-2 cursor-pointer transition-all hover:shadow-md ${
            !selectedCategory ? 'ring-2 ring-orange-500 bg-orange-50 dark:bg-orange-900/20' : ''
          }`}
          onClick={() => onCategorySelect(null)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
                <LayoutGrid className="h-4 w-4 text-white" />
              </div>
              <div>
                <div className="font-medium text-gray-900 dark:text-gray-100">All Products</div>
              </div>
            </div>
            <Badge variant="secondary" size="sm">
              {totalProducts}
            </Badge>
          </div>
        </Card>

        {/* Category Items */}
        {loading ? (
          <div className="space-y-2">
            {[1, 2, 3].map(i => (
              <div key={i} className="p-3 bg-gray-100 dark:bg-gray-700 rounded-lg animate-pulse">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gray-300 dark:bg-gray-600 rounded-lg"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded mb-1"></div>
                    <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-2/3"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          categories.map(category => {
            const isEmpty = category.productCount === 0
            const isDisabled = isEmpty && selectedCategory !== category.name
            
            return (
              <Card
                key={category.id}
                className={`p-2 transition-all ${
                  isEmpty 
                    ? 'opacity-60 hover:opacity-80' 
                    : 'cursor-pointer hover:shadow-md'
                } ${
                  selectedCategory === category.name 
                    ? 'ring-2 ring-orange-500 bg-orange-50 dark:bg-orange-900/20' 
                    : ''
                }`}
                onClick={() => !isDisabled && onCategorySelect(category.name)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 bg-gradient-to-r ${
                      isEmpty 
                        ? 'from-gray-400 to-gray-500' 
                        : 'from-blue-500 to-purple-500'
                    } rounded-lg flex items-center justify-center`}>
                      {isEmpty ? (
                        <AlertCircle className="h-4 w-4 text-white" />
                      ) : (
                        <span className="text-white text-sm font-semibold">
                          {category.name.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900 dark:text-gray-100 truncate flex items-center gap-2">
                        {category.name}
                        {isEmpty && (
                          <span className="text-xs text-gray-400 dark:text-gray-500 italic">
                            (empty)
                          </span>
                        )}
                      </div>
                      {/* Only show description for empty categories, make it more compact */}
                      {isEmpty && (
                        <div className="text-xs text-gray-400 dark:text-gray-500 italic">
                          No products
                        </div>
                      )}
                    </div>
                  </div>
                  <Badge 
                    variant={isEmpty ? "outline" : "secondary"} 
                    size="sm"
                    className={isEmpty ? 'text-gray-400' : ''}
                  >
                    {category.productCount}
                  </Badge>
                </div>
              </Card>
            )
          })
        )}
      </div>

      {/* Footer Stats */}
      <div className="border-t border-gray-200 dark:border-gray-700 p-4">
        <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
          <div className="flex justify-between">
            <span>Total Categories:</span>
            <span className="font-medium">{categories.length}</span>
          </div>
          <div className="flex justify-between">
            <span>Selected:</span>
            <span className="font-medium text-orange-600 dark:text-orange-400">
              {selectedCategory || 'All'}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}